import {useState} from 'react'
import {useDialog} from '@/hooks/useDialog'
import {Clock, CheckCircle, XCircle, Ban, User, KeyRound, Calendar} from 'lucide-react'
import {useParams, useLocation} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {SmallText} from '@/components/shared/SmallText'
import {Strong} from '@/components/shared/Strong'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {CommentDialog} from '@/components/shared/CommentDialog'
import {ApprovalChain} from '@/components/ApprovalChain'
import {EscalationTimelineCard} from '@/components/shared/EscalationTimelineCard'
import {EscalationStepType} from '@/types/escalation'
import {
    ApprovalStepStatus,
    RequestItemStatus,
    RequestStatus,
} from '@/types/access-request'
import {CONCEPT_ICON} from '@/lib/concepts'
import {formatTimestamp, formatDateOnly} from '@/lib/date-utils'
import {PageLayout} from '@/components/shared/PageLayout'
import {IconText} from '@/components/shared/IconText'
import {MutedText} from '@/components/shared/MutedText'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {TimelineItem} from '@/components/shared/TimelineItem'
import {StatusText} from '@/components/shared/StatusText'
import {useAccessRequest} from '@/features/access-requests/hooks/useAccessRequest'
import {useAccessRequestPermissions} from '@/features/access-requests/hooks/useAccessRequestPermissions'
import {useAccessRequestMutations} from '@/features/access-requests/hooks/useAccessRequestMutations'

export function AccessRequestDetail() {
    const {id} = useParams<{ id: string }>()
    const location = useLocation()
    const i18n = useI18n()
    usePageTitle(i18n.accessRequests.detailPageTitle)

    const requestStatusBadge = (status: RequestStatus) => {
        switch (status) {
            case RequestStatus.SUBMITTED:
                return <Badge data-testid={`status-badge-${status}`} variant="outline" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.accessRequests.statusSubmitted}</Badge>
            case RequestStatus.IN_PROGRESS:
                return <Badge data-testid={`status-badge-${status}`} variant="secondary" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.accessRequests.statusInProgress}</Badge>
            case RequestStatus.COMPLETED:
                return <Badge data-testid={`status-badge-${status}`} variant="success" className="gap-1"><CheckCircle
                    className="h-3 w-3"/>{i18n.accessRequests.statusCompleted}</Badge>
            case RequestStatus.CANCELLED:
                return <Badge data-testid={`status-badge-${status}`} variant="destructive" className="gap-1"><Ban
                    className="h-3 w-3"/>{i18n.accessRequests.statusCancelled}</Badge>
        }
    }

    const itemStatusBadge = (status: RequestItemStatus) => {
        switch (status) {
            case RequestItemStatus.PENDING_APPROVAL:
                return <Badge data-testid={`item-status-badge-${status}`} variant="outline" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.accessRequests.statusPendingApproval}</Badge>
            case RequestItemStatus.APPROVED:
                return <Badge data-testid={`item-status-badge-${status}`} variant="secondary" className="gap-1"><CheckCircle
                    className="h-3 w-3"/>{i18n.accessRequests.statusApproved}</Badge>
            case RequestItemStatus.FULFILLED:
                return <Badge data-testid={`item-status-badge-${status}`} variant="success" className="gap-1"><CheckCircle
                    className="h-3 w-3"/>{i18n.accessRequests.statusFulfilled}</Badge>
            case RequestItemStatus.REJECTED:
                return <Badge data-testid={`item-status-badge-${status}`} variant="destructive" className="gap-1"><XCircle
                    className="h-3 w-3"/>{i18n.accessRequests.statusRejected}</Badge>
            case RequestItemStatus.CANCELLED:
                return <Badge data-testid={`item-status-badge-${status}`} variant="secondary" className="gap-1"><Ban
                    className="h-3 w-3"/>{i18n.accessRequests.statusCancelled}</Badge>
        }
    }

    const [approveDialogItem, setApproveDialogItem] = useState<string | null>(null)
    const [rejectDialogItem, setRejectDialogItem] = useState<string | null>(null)
    const [cancelDialogItem, setCancelDialogItem] = useState<string | null>(null)
    const cancelRequestDialog = useDialog()

    const isMyRoute = location.pathname.startsWith(`${routes.my.root()}/`)
    const backPath = isMyRoute ? routes.my.requests.list() : routes.accessGovernance.accessRequests.list()

    const {request, isLoading, error} = useAccessRequest(id)
    const {canApprove, canDelete} = useAccessRequestPermissions()

    const {approve, reject, cancelItem, cancelRequest} = useAccessRequestMutations(id, {
        isMyRoute,
        onApproveSuccess: () => setApproveDialogItem(null),
        onRejectSuccess: () => setRejectDialogItem(null),
        onCancelItemSuccess: () => setCancelDialogItem(null),
        onCancelRequestSuccess: cancelRequestDialog.close,
    })

    const hasPendingItems = request?.items.some(i => i.status === RequestItemStatus.PENDING_APPROVAL) ?? false
    const canCancel = canDelete && hasPendingItems

    return (
        <PageLayout
            variant="detail"
            title={i18n.accessRequests.title}
            icon={CONCEPT_ICON.accessRequest}
            backTo={backPath}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(request, 'ACCESS_REQUEST') : undefined)}
            data-testid="access-request-detail-page"
            description={request ? (
                <InlineRow>
                    {requestStatusBadge(request.status)}
                </InlineRow>
            ) : undefined}
            actions={canCancel ? (
                <Button data-testid="btn-cancel-request" variant="destructive" onClick={() => {
                    cancelRequestDialog.open()
                }}>
                    <IconText icon={Ban}>{i18n.accessRequests.cancelRequest}</IconText>
                </Button>
            ) : undefined}
        >
            {request && <>
            <DetailGrid columns={2} gap="lg" className="mb-6">
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-request-details">
                            <MutedText size="sm" weight="medium">{i18n.accessRequests.requestDetails}</MutedText>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <IconText icon={User}>
                            <MutedText>{i18n.accessRequests.requester}:</MutedText>
                            <Strong>{request.requester?.displayName ?? '—'}</Strong>
                        </IconText>
                        <IconText icon={User}>
                            <MutedText>{i18n.accessRequests.beneficiary}:</MutedText>
                            <Strong>{request.beneficiary?.displayName ?? '—'}</Strong>
                        </IconText>
                        <IconText icon={Calendar}>
                            <MutedText>{i18n.common.createdAt}:</MutedText>
                            <MutedText className="text-foreground">{formatTimestamp(request.createdAt)}</MutedText>
                        </IconText>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-justification">
                            <MutedText size="sm" weight="medium">{i18n.accessRequests.justification}</MutedText>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <SmallText>{request.justification}</SmallText>
                    </CardContent>
                </Card>
            </DetailGrid>

            <Card>
                <CardHeader>
                    <CardTitle data-testid="card-title-request-items">{i18n.accessRequests.requestItems({count: request.items.length})}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Stack>
                        {request.items.map(item => (
                            <Card key={item.id} data-testid={`request-item-${item.id}`}>
                                <CardContent className="pt-6">
                                    <Stack>
                                        <InlineRow align="start" justify="between">
                                            <Stack gap="sm">
                                                <IconText icon={KeyRound}>
                                                    <Strong>{item.accessProfile?.displayName ?? '—'}</Strong>
                                                    <MutedText>({item.resource?.displayName ?? '—'})</MutedText>
                                                </IconText>
                                                <InlineRow gap="xl">
                                                    {item.expiresAt ? (
                                                        <MutedText>{i18n.accessRequests.untilDate({date: formatDateOnly(item.expiresAt)})}</MutedText>
                                                    ) : (
                                                        <MutedText>{i18n.common.permanent}</MutedText>
                                                    )}
                                                    {item.matchedPolicyName && (
                                                        <MutedText size="xs">{i18n.approvalChain.matchedPolicy}: {item.matchedPolicyName}</MutedText>
                                                    )}
                                                </InlineRow>
                                                <InlineRow>
                                                    {itemStatusBadge(item.status)}
                                                </InlineRow>
                                            </Stack>

                                            <InlineRow>
                                                {item.status === RequestItemStatus.PENDING_APPROVAL && canCancel && (
                                                    <Button data-testid={`btn-cancel-item-${item.id}`} size="sm"
                                                            variant="outline" onClick={() => {
                                                        setCancelDialogItem(item.id)
                                                    }}>
                                                        <Icon icon={Ban} size="xs" className="mr-1"/>
                                                        {i18n.common.cancel}
                                                    </Button>
                                                )}
                                            </InlineRow>
                                        </InlineRow>

                                        {item.approvalChain && item.approvalChain.length > 0 && (
                                            <TimelineItem className="mt-4 ml-1">
                                                <MutedText size="xs" block weight="medium" uppercase className="mb-3">
                                                    {i18n.approvalChain.title}
                                                </MutedText>
                                                <ApprovalChain
                                                    steps={item.approvalChain}
                                                    itemId={item.id}
                                                    onApprove={canApprove ? (itemId) => {
                                                        setApproveDialogItem(itemId)
                                                    } : undefined}
                                                    onReject={canApprove ? (itemId) => {
                                                        setRejectDialogItem(itemId)
                                                    } : undefined}
                                                />
                                            </TimelineItem>
                                        )}
                                        {item.approvalChain
                                            ?.filter(step => step.status === ApprovalStepStatus.ACTIVE)
                                            .map(step => (
                                                <EscalationTimelineCard
                                                    key={`escalation-${step.id}`}
                                                    stepId={step.id}
                                                    stepType={EscalationStepType.ACCESS_REQUEST}
                                                    escalationLevel={step.escalationLevel}
                                                    nextDeadlineAt={step.nextDeadlineAt}
                                                    platformOwnerName={step.platformOwnerName}
                                                    data-testid={`escalation-timeline-${step.id}`}
                                                />
                                            ))}

                                        {item.cancelReason && (
                                            <StatusText tone="destructive" size="sm" className="mt-2">
                                                {i18n.accessRequests.cancelled} {item.cancelReason}
                                            </StatusText>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </CardContent>
            </Card>

            <CommentDialog
                open={!!approveDialogItem}
                title={i18n.accessRequests.approveItem}
                commentLabel={i18n.accessRequests.commentOptional}
                commentPlaceholder={i18n.accessRequests.commentPlaceholder}
                commentRequired={false}
                confirmLabel={i18n.common.approve}
                confirmingLabel={i18n.accessRequests.approving}
                confirming={approve.isPending}
                onConfirm={(comment) => {
                    if (approveDialogItem) {
                        approve.mutate({
                            itemId: approveDialogItem,
                            body: {comment: comment || undefined},
                        })
                    }
                }}
                onCancel={() => setApproveDialogItem(null)}
                data-testid="approve-dialog"
            />

            <CommentDialog
                open={!!rejectDialogItem}
                title={i18n.accessRequests.rejectItem}
                commentLabel={i18n.accessRequests.reasonRequired}
                commentPlaceholder={i18n.accessRequests.rejectReasonPlaceholder}
                commentRequired={true}
                confirmLabel={i18n.common.reject}
                confirmingLabel={i18n.accessRequests.rejecting}
                confirmVariant="destructive"
                confirming={reject.isPending}
                onConfirm={(comment) => {
                    if (rejectDialogItem) {
                        reject.mutate({
                            itemId: rejectDialogItem,
                            body: {comment},
                        })
                    }
                }}
                onCancel={() => setRejectDialogItem(null)}
                data-testid="reject-dialog"
            />

            <CommentDialog
                open={!!cancelDialogItem}
                title={i18n.accessRequests.cancelItem}
                commentLabel={i18n.accessRequests.cancelReason}
                commentPlaceholder={i18n.accessRequests.cancelReasonPlaceholder}
                commentRequired={true}
                confirmLabel={i18n.accessRequests.cancelItem}
                confirmingLabel={i18n.accessRequests.cancelling}
                confirmVariant="destructive"
                confirming={cancelItem.isPending}
                onConfirm={() => {
                    if (cancelDialogItem) {
                        cancelItem.mutate({
                            itemId: cancelDialogItem,
                        })
                    }
                }}
                onCancel={() => setCancelDialogItem(null)}
                data-testid="cancel-item-dialog"
            />

            <CommentDialog
                open={cancelRequestDialog.isOpen}
                title={i18n.accessRequests.cancelEntireRequest}
                commentLabel={i18n.accessRequests.cancelReason}
                commentPlaceholder={i18n.accessRequests.cancelReasonPlaceholder}
                commentRequired={true}
                confirmLabel={i18n.accessRequests.cancelRequest}
                confirmingLabel={i18n.accessRequests.cancelling}
                confirmVariant="destructive"
                confirming={cancelRequest.isPending}
                onConfirm={() => {
                    cancelRequest.mutate()
                }}
                onCancel={cancelRequestDialog.close}
                data-testid="cancel-request-dialog"
            />
        </>}
        </PageLayout>
    )
}
